import { useState, useRef, useEffect } from 'react';
import {
  checkWritingFile,
  getMimeType,
  idbClear,
  idbLoad,
  idbReplaceAll,
  idbSave,
  readFileAsDataURL,
  type IdbItem,
} from '@/lib/writingFiles';

export interface PageItem {
  id: string;
  file: File;
  previewUrl: string | null; // data URL for images; null for PDFs
}

// Upload's ?debugUpload=1 logging. Callers that don't need it leave it out.
export interface WritingPagesDebug {
  enabled: boolean;
  log: (line: string) => void;      // React state only (processFiles)
  write: (line: string) => void;    // localStorage + React state
  persist: (line: string) => void;  // localStorage only, before any React call (survives unmount)
}

interface Options {
  idbName: string;
  maxPages: number;
  onFileError?: (message: string) => void;
  debug?: WritingPagesDebug;
}

// Page picking for writing uploads, including the mobile camera fixes: a fresh off-screen
// <input> per pick, change+input listeners with dedup, and IndexedDB persistence so files
// survive the page reload Android Chrome does when the native picker opens.
export function useWritingPages({ idbName, maxPages, onFileError, debug }: Options) {
  const [pages, setPages] = useState<PageItem[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  // Dedup flag — prevents double-processing when both onChange and onInput fire for the same selection.
  const processingRef = useRef(false);
  // Set to true once the IDB restore attempt completes (success or fail).
  // Guards the pages-sync useEffect so it doesn't race with restore on first mount.
  const idbRestoredRef = useRef(false);

  // Recreated on every render — always captures the current closure (debug, onFileError, etc.).
  // setPages uses a functional updater so it always reads live state.
  const processFiles = (inputEl: HTMLInputElement, source = 'unknown') => {
    const dbg = (msg: string) => {
      if (!debug?.enabled) return;
      const ts = new Date().toISOString().slice(11, 23);
      debug.log(`${ts} [${source}] ${msg}`);
    };

    const rawLength = inputEl.files?.length ?? 0;
    dbg(`fired · files.length=${rawLength}`);

    const selected = Array.from(inputEl.files ?? []);

    // Ignore empty FileList without resetting the input.
    // Android Chrome fires 'change' before the content:// URI resolves.
    // Resetting input.value destroys the URI reference; the real file never arrives.
    if (selected.length === 0) {
      dbg('empty FileList → ignored (no reset)');
      return;
    }

    // Dedup: engage only after confirming there are files.
    if (processingRef.current) {
      dbg('dedup: already processing → skipped');
      return;
    }
    processingRef.current = true;
    queueMicrotask(() => { processingRef.current = false; });

    dbg(`processing ${selected.length} file(s)`);
    setFileError(null);

    const accepted: PageItem[] = [];
    const previewQueue: { id: string; promise: Promise<string> }[] = [];
    let firstError: string | null = null;

    for (const f of selected) {
      try {
        const mime = getMimeType(f);
        dbg(`file: name="${f.name}" type="${f.type}" size=${f.size} → mime="${mime}"`);

        const problem = checkWritingFile(f, mime);
        if (problem) {
          firstError = firstError ?? problem;
          dbg(`rejected: ${problem}`);
          continue;
        }

        const id = crypto.randomUUID();
        accepted.push({ id, file: f, previewUrl: null });
        dbg(`accepted: id=${id.slice(0, 8)}`);

        if (mime.startsWith('image/')) {
          previewQueue.push({ id, promise: readFileAsDataURL(f) });
        }
      } catch (err) {
        dbg(`error processing file: ${err}`);
      }
    }

    // Reset AFTER starting all FileReaders — they now hold the data.
    inputEl.value = '';

    if (firstError) {
      setFileError(firstError);
      onFileError?.(firstError);
    }
    dbg(`accepted=${accepted.length} rejected=${selected.length - accepted.length}`);
    if (accepted.length === 0) return;

    // Functional updater — always reads live state, never the stale closure.
    setPages(prev => {
      const remaining = maxPages - prev.length;
      dbg(`setPages: prev.length=${prev.length} remaining=${remaining} adding=${Math.min(accepted.length, remaining)}`);
      if (remaining <= 0) return prev;
      return [...prev, ...accepted.slice(0, remaining)];
    });

    for (const { id, promise } of previewQueue) {
      promise
        .then(previewUrl => {
          dbg(`preview ok: id=${id.slice(0, 8)}`);
          setPages(prev =>
            prev.some(p => p.id === id)
              ? prev.map(p => p.id === id ? { ...p, previewUrl } : p)
              : prev
          );
        })
        .catch((err) => {
          dbg(`preview failed: id=${id.slice(0, 8)} err=${err}`);
          // previewUrl stays null — thumbnail shows file icon
        });
    }
  };

  // On every mount, check IDB for files saved by the previous page context.
  // This fires after Android Chrome reloads the page when returning from the picker.
  useEffect(() => {
    idbLoad(idbName).then(items => {
      idbRestoredRef.current = true; // ungate pages-sync useEffect
      if (items.length === 0) return;
      const TEN_MIN = 10 * 60 * 1000;
      const fresh = items.filter(item => Date.now() - item.ts < TEN_MIN);
      if (fresh.length === 0) { idbClear(idbName).catch(() => {}); return; }
      // Don't clear IDB here — pages-sync useEffect will keep it current.
      if (debug?.enabled) {
        const ts = new Date().toISOString().slice(11, 23);
        debug.write(`${ts} [idb-restore] ${fresh.length} file(s)`);
      }
      const pageItems: PageItem[] = fresh.map(({ id, file }) => ({ id, file, previewUrl: null }));
      setPages(pageItems);
      for (const { id, file, mimeType } of fresh) {
        if (mimeType.startsWith('image/')) {
          readFileAsDataURL(file).then(previewUrl => {
            setPages(prev =>
              prev.some(p => p.id === id)
                ? prev.map(p => p.id === id ? { ...p, previewUrl } : p)
                : prev
            );
          }).catch(() => {});
        }
      }
    }).catch(() => { idbRestoredRef.current = true; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep IDB in sync with current pages so each Android-reload sees the full list.
  // Guarded by idbRestoredRef so this doesn't race with the restore above on first mount
  // (initial render has pages=[] which would otherwise wipe IDB before restore reads it).
  useEffect(() => {
    if (!idbRestoredRef.current) return;
    const items: IdbItem[] = pages.map(p => ({
      id: p.id,
      file: p.file,
      mimeType: getMimeType(p.file),
      ts: Date.now(),
    }));
    idbReplaceAll(idbName, items).catch(() => {});
  }, [pages, idbName]);

  const openFilePicker = () => {
    debug?.write(`[openFilePicker] creating input`);
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.pdf';
    input.multiple = maxPages > 1;
    input.style.cssText = 'position:fixed;top:-200px;left:-200px;width:1px;height:1px;';
    document.body.appendChild(input);

    const onEvent = (e: Event) => {
      const filesLen = input.files?.length ?? 0;
      const ts = new Date().toISOString().slice(11, 23);

      // Write to localStorage BEFORE any React call — survives unmount.
      debug?.persist(`${ts} [${e.type}] files=${filesLen}`);

      // Persist to IDB before processFiles resets input.value.
      // If Android Chrome reloads the page on return from the picker, IDB survives
      // while React state (and even the DOM banner above) does not.
      if (filesLen > 0 && input.files) {
        const idbTs = Date.now();
        const idbItems: IdbItem[] = Array.from(input.files).map(f => ({
          id: crypto.randomUUID(),
          file: f,
          mimeType: getMimeType(f),
          ts: idbTs,
        }));
        idbSave(idbName, idbItems).catch(() => {});
      }

      processFiles(input, e.type);
      setTimeout(() => { try { document.body.removeChild(input); } catch { /* already removed */ } }, 300);
    };

    input.addEventListener('change', onEvent);
    input.addEventListener('input',  onEvent);
    debug?.write(`[openFilePicker] calling click()`);
    input.click();
  };

  const removePage = (id: string) => {
    setPages(prev => prev.filter(p => p.id !== id));
  };

  const clearPersisted = () => idbClear(idbName).catch(() => {});

  return { pages, setPages, fileError, setFileError, openFilePicker, removePage, clearPersisted };
}
